import { Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import Swal from 'sweetalert2';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { mainColors } from '../../helpers/colors';
import {
  deleteTokenActionRequest,
  setTokensSortDataAction,
  sortTokensAction,
} from '../../store/token/tokenActions';
import { setAlertAction } from '../../store/alert/alertActions';
import useMercurxPagination from '../../hooks/useMercurxPagination';
import MercurXIcons from '../MercurXIcons/MercurXIcons';
import MercurXSortableHeader from '../MercurXSortableHeader/MercurXSortableHeader';
import MercurXTablePagination from '../MercurXTablePagination/MercurXTablePagination';
import { TOKENS_SORT_TYPES, sortKeys } from '../../pages/Tokens/tokensConstants';
import './MercurXTokenTable.scss';

function MercurXTokenTable(props) {
  const {
    deleteTokenRequest,
    setAlert,
    tokens,
    filteredTokens,
    tokensSortData,
    setTokensSortData,
    sortTokens,
  } = props;
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem('user'))?.user;
  const sourceData = Array.isArray(filteredTokens)
    ? filteredTokens
    : Array.isArray(tokens)
      ? tokens
      : [];
  const visibleTokens = sourceData.filter((item) => item?.status !== false);
  const {
    currentPage,
    totalPages,
    visiblePages,
    pagedData: renderedTokens,
    rowOffset,
    isFirstPage,
    isLastPage,
    goToPage,
    goToFirstPage,
    goToPrevPage,
    goToNextPage,
    goToLastPage,
  } = useMercurxPagination(visibleTokens, 10, 5);

  function scrollToTokensContainer() {
    const tokensContainer = document.getElementById('tokens-container');
    if (tokensContainer) {
      tokensContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const handlePageChange = (page) => {
    goToPage(page);
    scrollToTokensContainer();
  };

  const handleFirstPage = () => {
    goToFirstPage();
    scrollToTokensContainer();
  };

  const handlePrevPage = () => {
    goToPrevPage();
    scrollToTokensContainer();
  };

  const handleNextPage = () => {
    goToNextPage();
    scrollToTokensContainer();
  };

  const handleLastPage = () => {
    goToLastPage();
    scrollToTokensContainer();
  };

  const handleSort = (sortKey, defaultSortType) => {
    const nextSortType = tokensSortData.sortKey === sortKey
      ? (tokensSortData.sortType === TOKENS_SORT_TYPES.ASCENDING
        ? TOKENS_SORT_TYPES.DESCENDING
        : TOKENS_SORT_TYPES.ASCENDING)
      : defaultSortType;

    setTokensSortData({
      sortKey,
      sortType: nextSortType,
    });
    sortTokens();
  };

  function editToken(token) {
    history.push({
      pathname: '/tokens/edit-token',
      state: {
        token,
      },
    });
  }

  function deleteToken(tokenID, tokenIndex) {
    Swal.fire({
      icon: 'warning',
      iconColor: mainColors.warning,
      text: 'This process can not be undo. Are you sure?',
      showCancelButton: true,
    }).then((control) => {
      if (control.isConfirmed) {
        deleteTokenRequest({
          tokenID,
          tokenIndex,
        });
      }
    });
  }

  function shortenText(value, startLength = 8, endLength = 6) {
    if (!value) {
      return '-';
    }

    const text = String(value);
    if (text.length <= startLength + endLength + 3) {
      return text;
    }

    return `${text.slice(0, startLength)}...${text.slice(-endLength)}`;
  }

  async function copyToClipboard(text, label = 'Value') {
    if (!text || text === '-') {
      return;
    }

    const normalizedText = String(text);
    let isCopied = false;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalizedText);
        isCopied = true;
      }
    } catch (error) {
      // fallback below
    }

    if (!isCopied) {
      const textArea = document.createElement('textarea');
      textArea.value = normalizedText;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      isCopied = document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    if (isCopied) {
      setAlert({
        title: 'Success!',
        text: `${label} Copied To Clipboard`,
        variant: 'success',
        outTimeMS: 3000,
      });
    }
  }

  function renderCopyableText(value, options = {}) {
    const { startLength = 8, endLength = 6, label = 'Value' } = options;
    if (!value) {
      return '-';
    }

    const text = String(value);
    return (
      <span
        role="button"
        tabIndex={0}
        className="copyable-cell-text"
        title={`${text} (Click to copy)`}
        onClick={() => copyToClipboard(text, label)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            copyToClipboard(text, label);
          }
        }}
      >
        {shortenText(text, startLength, endLength)}
      </span>
    );
  }

  function calculateFormat(value) {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
      return '-';
    }

    const formatBalance = (Math.floor((Number(value) / 10000) * 100) / 100).toString().split('.');
    const firstValueFormatBalance = Number(formatBalance[0]).toLocaleString('tr-TR');
    const secondValueFormatBalance = formatBalance[1] || '0';
    return `${firstValueFormatBalance},${secondValueFormatBalance}`;
  }

  function formatUsd(value) {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
      return '-';
    }

    return Number(value).toLocaleString('tr-TR', {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'symbol',
      useGrouping: true,
      minimumFractionDigits: 4,
    });
  }

  return (
    <>
      <Table className="tokens-table-design" responsive hover borderless>
        <thead>
          <tr className="text-t-body-color text-fs-tag">
            <th>#</th>
            {sortKeys.map((sortKeyConfig, index) => (
              <th key={sortKeyConfig.name}>
                <MercurXSortableHeader
                  label={sortKeyConfig.name}
                  isActive={tokensSortData.sortKey === index}
                  isAscending={tokensSortData.sortType === TOKENS_SORT_TYPES.ASCENDING}
                  onClick={() => handleSort(index, sortKeyConfig.defaultSortType)}
                />
              </th>
            ))}
            {user?.is_superuser && (
              <>
                <th />
                <th />
              </>
            )}
          </tr>
        </thead>
        {renderedTokens?.length ? (
          <tbody>
            {renderedTokens.map((item, index) => (
                <tr className="text-t-head-color" key={item.id || index}>
                  <td>{rowOffset + index + 1}</td>
                  <td>{item.name || '-'}</td>
                  <td>{item.symbol || '-'}</td>
                  <td>{item.decimals ?? '-'}</td>
                  <td>{renderCopyableText(item.address, { label: 'Token Address' })}</td>
                  <td>
                    {renderCopyableText(item.owner_address, { label: 'Token Owner Address' })}
                  </td>
                  <td>{calculateFormat(item.total_supply)}</td>
                  <td>{item.price_in_mercurx ?? '-'}</td>
                  <td>{formatUsd(item.price_in_usd)}</td>
                  <td>{calculateFormat(item.distribution)}</td>
                  <td>{item.current_price ?? '-'}</td>
                  {user?.is_superuser && (
                    <>
                      <td>
                        <div className="cursor-pointer mt-1" onClick={() => editToken(item)}>
                          <MercurXIcons
                            name="BsPencilSquare"
                            size={18}
                            color={mainColors.primary}
                          />
                        </div>
                      </td>
                      <td>
                        <div
                          className="cursor-pointer mt-1"
                          onClick={() => deleteToken(item.id, index)}
                        >
                          <MercurXIcons name="BsTrash3" size={18} color={mainColors.danger} />
                        </div>
                      </td>
                    </>
                  )}
                </tr>
            ))}
          </tbody>
        ) : null}
      </Table>

      {!renderedTokens?.length && (
        <div className="d-flex text-fs-body-md text-t-body-color justify-content-center">
          No token found according to search results.
        </div>
      )}

      <MercurXTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        visiblePages={visiblePages}
        isFirstPage={isFirstPage}
        isLastPage={isLastPage}
        onFirstPage={handleFirstPage}
        onPrevPage={handlePrevPage}
        onPageChange={handlePageChange}
        onNextPage={handleNextPage}
        onLastPage={handleLastPage}
      />
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    tokens: state.tokenReducer.tokens,
    filteredTokens: state.tokenReducer.filteredTokens,
    tokensSortData: state.tokenReducer.tokensSortData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteTokenRequest: (payload) => {
      dispatch(deleteTokenActionRequest(payload));
    },
    setTokensSortData: (payload) => {
      dispatch(setTokensSortDataAction(payload));
    },
    sortTokens: (payload) => {
      dispatch(sortTokensAction(payload));
    },
    setAlert: (payload) => {
      dispatch(setAlertAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MercurXTokenTable);
